/**
 * Custom hook for managing push notifications
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { pushNotificationService } from '@/services/pushNotification.service';
import { 
  NotificationPermissionState, 
  NotificationPayload, 
  NotificationSettings,
  NotificationTopics
} from '@/types/notifications';
import toast from 'react-hot-toast';

interface UsePushNotificationsReturn {
  // State
  permissionState: NotificationPermissionState;
  fcmToken: string | null;
  isLoading: boolean;
  settings: NotificationSettings;
  error: string | null;
  
  // Actions
  requestPermission: () => Promise<void>;
  enableNotifications: () => Promise<void>;
  disableNotifications: () => Promise<void>;
  updateSettings: (settings: NotificationSettings) => void;
  refreshToken: () => Promise<void>;
  
  // Status
  isSupported: boolean;
  isEnabled: boolean;
  initialize: () => Promise<void>;
}

export const usePushNotifications = (userId?: string): UsePushNotificationsReturn => {
  const [permissionState, setPermissionState] = useState<NotificationPermissionState>({
    permission: 'default',
    isSupported: false,
    isEnabled: false
  });
  const [fcmToken, setFcmToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<NotificationSettings>({
    enabled: false,
    topics: {
      [NotificationTopics.GENERAL]: true,
      [NotificationTopics.ALERTS]: true,
      [NotificationTopics.UPDATES]: false,
      [NotificationTopics.CHAT]: true,
      [NotificationTopics.SYSTEM]: false
    },
    sound: true,
    vibrate: true
  });
  const initialized = useRef(false);

  // Check if notifications are supported
  const isSupported = permissionState.isSupported;
  const isEnabled = permissionState.isEnabled && settings.enabled;

  /**
   * Initialize settings from localStorage
   */
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storedSettings = pushNotificationService.getNotificationSettings();
      setSettings(storedSettings);
    }
  }, []);

  /**
   * Handle foreground messages
   */
  const handleForegroundMessage = useCallback((payload: NotificationPayload) => {
    console.log('Foreground message received:', payload);
    
    // Show notification if settings allow it
    if (settings.enabled) {
      pushNotificationService.showNotification(payload);
      
      // Also show as toast for better UX
      toast.success(
        payload.notification?.body || 'New notification received',
        {
          duration: 4000,
          icon: '🔔',
        }
      );
    }
  }, [settings.enabled]);

  /**
   * Request notification permission
   */
  const requestPermission = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const permission = await pushNotificationService.requestPermission();
      setPermissionState(pushNotificationService.getPermissionState());
      
      if (permission === 'granted') {
        toast.success('Notifications enabled successfully!');
      } else if (permission === 'denied') {
        toast.error('Notification permission denied. You can enable it later in browser settings.');
      } else {
        toast('Notification permission dismissed.');
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
      setError('Failed to request notification permission');
      toast.error('Failed to request notification permission');
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Enable notifications (get token and register)
   */
  const enableNotifications = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // First check permission
      const currentPermission = pushNotificationService.getPermissionState();
      if (currentPermission.permission !== 'granted') {
        const permission = await pushNotificationService.requestPermission();
        if (permission !== 'granted') {
          toast.error('Notification permission is required');
          setIsLoading(false);
          return;
        }
      }
      // Initialize to get token
      const token = await pushNotificationService.initialize(userId);
      
      if (token) {
        setFcmToken(token);
        const newSettings = { ...settings, enabled: true };
        setSettings(newSettings);
        pushNotificationService.updateNotificationSettings(newSettings);
        toast.success('Push notifications enabled!');
      } else {
        setError('Failed to get push token');
        toast.error('Failed to enable notifications. Please check your browser settings.');
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
      setError('Failed to enable notifications');
     
    } finally {
      setIsLoading(false);
    }
  }, [userId, settings]);

  /**
   * Disable notifications
   */
  const disableNotifications = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      await pushNotificationService.deleteToken();
      setFcmToken(null);
      
      const newSettings = { ...settings, enabled: false };
      setSettings(newSettings);
      pushNotificationService.updateNotificationSettings(newSettings);
      
      toast.success('Push notifications disabled');
    } catch (error) {
      console.error('Error disabling notifications:', error);
      setError('Failed to disable notifications');
      
    } finally {
      setIsLoading(false);
    }
  }, [settings]);

  /**
   * Update notification settings
   */
  const updateSettings = useCallback((newSettings: NotificationSettings) => {
    setSettings(newSettings);
    pushNotificationService.updateNotificationSettings(newSettings);
    toast.success('Notification settings updated');
  }, []);

  /**
   * Refresh FCM token
   */
  const refreshToken = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = await pushNotificationService.getToken();
      if (token) {
        setFcmToken(token);
        if (userId) {
          await pushNotificationService.registerTokenWithBackend(token, userId);
        }
        toast.success('Token refreshed successfully');
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      setError('Failed to refresh token');
      toast.error('Failed to refresh token');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  /**
   * Initialize hook - check support and get current state
   */
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    initialize();
  }, []);

  
  const initialize = async () => {
    try {
      // Check if notifications are supported
      const supported = await pushNotificationService.isSupported();
      
      // Update permission state
      const currentPermissionState = pushNotificationService.getPermissionState();
      setPermissionState({
        ...currentPermissionState,
        isSupported: supported
      });

      if (!supported) {
        console.log('Push notifications not supported in this browser');
        return;
      }

      // Get stored token
      const storedToken = pushNotificationService.getStoredToken();
      if (storedToken) {
        setFcmToken(storedToken.token);
      }
    } catch (error) {
      console.error('Error initializing push notifications hook:', error);
    }
  };

  /**
   * Set up foreground message handler when enabled
   */
  useEffect(() => {
    if (!isSupported || !settings.enabled || !permissionState.isEnabled) {
      return;
    }

    let unsubscribe: (() => void) | null = null;

    const setupMessageHandler = () => {
      unsubscribe = pushNotificationService.onMessage(handleForegroundMessage);
    };

    setupMessageHandler();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [isSupported, settings.enabled, permissionState.isEnabled, handleForegroundMessage]);

  /**
   * Listen for permission changes when tab becomes visible
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const currentState = pushNotificationService.getPermissionState();
        setPermissionState(prev => ({
          ...currentState,
          isSupported: prev.isSupported
        }));
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  return {
    // State
    permissionState,
    fcmToken,
    isLoading,
    settings,
    error,
    
    // Actions
    requestPermission,
    enableNotifications,
    disableNotifications,
    updateSettings,
    refreshToken,
    
    // Status
    isSupported,
    isEnabled,
    initialize
  };
};
